import { Grid } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC, useState } from 'react';

import { Caret } from 'src/Icons';
import { Label, LabelProps } from 'src/StatusLabel/Label';
import { StatusIcon } from 'src/StatusLabel/StatusIcon';
import { StatusLabelMenu } from 'src/StatusLabel/StatusLabelMenu';
import { StatusLabelClassNames, StatusLabelStatus } from 'src/types';

export interface Action {
  label: string;
  onClick: () => void;
}
export interface StatusLabelProps {
  /**
   * The status icon's accessible name, in the page's language. Without one the
   * icon is decorative, which is right when the label text already says the state.
   */
  iconLabel?: string;
  label: LabelProps['label'];
  status: `${StatusLabelStatus}`;
  options?: {
    fullLabel?: string;
    additionalInfo?: string[];
    isClick?: boolean;
  };
  actions: Action[];
}

export const StatusLabel: FC<StatusLabelProps> = ({ iconLabel, label, status, options = {}, actions }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // `SyntheticEvent`, not `MouseEvent`: Enter and Space run the same handler, and
  // a keyboard event carries the `currentTarget` the menu anchors to.
  const handleClick = (event: React.SyntheticEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickForClickLabel = (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    return actions[0] && actions[0].onClick();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasMenu = Boolean(Object.keys(options).length) || Boolean(actions.length);
  const opensPopover = hasMenu && !options.isClick;
  // Only a popover carrying actions is a menu. One carrying nothing but label
  // rows is a disclosure, so it claims neither the role nor `aria-haspopup`.
  const opensActionMenu = opensPopover && Boolean(actions.length);

  const activate = options.isClick ? handleClickForClickLabel : handleClick;

  /**
   * A native button acts on Enter's keydown and on Space's keyup, and the chip
   * has to match. Opening on Space's keydown moves focus into the menu while the
   * key is still held, and MUI's `ButtonBase` runs `onClick` on the keyup at
   * whatever holds focus then — so a press longer than the open transition ran
   * the menu's first action.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      activate(event);
      return;
    }
    // Space scrolls the page on a `div`, and the chip sits in a scrolling table.
    if (event.key === ' ') event.preventDefault();
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== ' ') return;
    event.preventDefault();
    activate(event);
  };

  /**
   * A label with something to do is a control. The chip's own click target is the
   * only route to the Awaiting-signature menu, so leaving it out of the tab order
   * put those actions beyond a keyboard. A label with nothing to do carries no
   * role at all: it is a phrase, and the consumer owns any list around it.
   */
  const semantics: React.HTMLAttributes<HTMLDivElement> = hasMenu
    ? {
        role: 'button',
        tabIndex: 0,
        onKeyDown: handleKeyDown,
        onKeyUp: handleKeyUp,
        ...(opensPopover ? { 'aria-expanded': Boolean(anchorEl) } : {}),
        ...(opensActionMenu ? { 'aria-haspopup': 'menu' as const } : {}),
      }
    : {};

  return (
    <>
      <Grid
        className={classNames(StatusLabelClassNames.ROOT, {
          [StatusLabelClassNames.ACTION]: status === StatusLabelStatus.ACTION,
          [StatusLabelClassNames.INCOMPLETE]: status === StatusLabelStatus.INCOMPLETE,
          [StatusLabelClassNames.IN_PROGRESS]: status === StatusLabelStatus.IN_PROGRESS,
          [StatusLabelClassNames.PARTNER]: status === StatusLabelStatus.PARTNER,
          [StatusLabelClassNames.SUCCESS]: status === StatusLabelStatus.SUCCESS,
          [StatusLabelClassNames.WARNING]: status === StatusLabelStatus.WARNING,
          [StatusLabelClassNames.HOVER_STATE]: hasMenu,
        })}
        onClick={options.isClick ? handleClickForClickLabel : handleClick}
        {...semantics}
      >
        <StatusIcon className={StatusLabelClassNames.STATUS_ICON} label={iconLabel} status={status} />
        <Label label={label} />
        {hasMenu && <Caret className={StatusLabelClassNames.ACTION_ICON} />}
      </Grid>
      {opensPopover && (
        <StatusLabelMenu actions={actions} anchorEl={anchorEl} handleClose={handleClose} options={options} />
      )}
    </>
  );
};
